(() => {
  if (window.havanaCartUpsellReady) return;
  window.havanaCartUpsellReady = true;
  const roots = new Set();
  const states = new WeakMap();
  const base = window.Shopify?.routes?.root || '/';
  let adding = false;
  let refreshTimer;
  let refreshSerial = 0;
  const visibility = new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) load(entry.target); });
  }, { rootMargin: '80px' });

  function message(root, text) { root.querySelector('[data-upsell-status]').textContent = text; }

  async function load(root, force = false) {
    const state = states.get(root);
    if (!state || !root.isConnected || root.hidden || !state.seed || state.loading || (state.loaded && !force)) return;
    state.loading = true;
    const serial = ++state.serial;
    const track = root.querySelector('[data-upsell-track]');
    track.setAttribute('aria-busy', 'true');
    if (!state.loaded) track.textContent = root.dataset.loading;
    try {
      const url = new URL(root.dataset.url, location.origin);
      url.searchParams.set('section_id', 'havana-cart-upsell-products');
      url.searchParams.set('product_id', state.seed);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      const response = await fetch(url, { signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (serial !== state.serial || !root.isConnected) return;
      const seen = new Set(state.exclude);
      const cards = [];
      doc.querySelectorAll('[data-upsell-product]').forEach(card => {
        const id = card.dataset.upsellProduct;
        if (cards.length >= 4 || seen.has(id)) return;
        seen.add(id);
        cards.push(document.importNode(card, true));
      });
      track.replaceChildren(...cards);
      root.hidden = cards.length === 0;
      root.closest('.opus-drawer-container')?.classList.toggle('havana-opus-upsell-ready', cards.length > 0);
      state.loaded = true;
    } catch (error) {
      if (serial !== state.serial || !root.isConnected) return;
      if (!state.loaded) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'cart-upsell__retry';
        retry.textContent = root.dataset.retry;
        retry.addEventListener('click', () => load(root, true));
        track.replaceChildren(retry);
      }
    } finally {
      if (serial === state.serial) {
        state.loading = false;
        track.setAttribute('aria-busy', 'false');
      }
    }
  }

  function mount(root) {
    if (states.has(root)) return;
    let exclude = [];
    try { exclude = JSON.parse(root.dataset.exclude || '[]').map(String); } catch (error) { exclude = []; }
    states.set(root, { seed: root.dataset.seed, exclude, loaded: false, loading: false, serial: 0 });
    roots.add(root);
    visibility.observe(root);
    root.addEventListener('change', event => {
      if (!event.target.matches('[data-upsell-variant]')) return;
      const card = event.target.closest('[data-upsell-product]');
      card.querySelector('[data-upsell-price]').innerHTML = event.target.selectedOptions[0].dataset.priceLabel;
    });
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-upsell-add]');
      if (button && root.contains(button)) add(root, button);
    });
  }

  async function add(root, button) {
    if (adding || button.disabled) return;
    adding = true;
    const original = button.textContent;
    const card = button.closest('[data-upsell-product]');
    const variant = card.querySelector('[data-upsell-variant]').value;
    button.textContent = root.dataset.adding;
    message(root, '');
    roots.forEach(el => el.querySelectorAll('[data-upsell-add]').forEach(btn => { btn.disabled = true; }));
    let committed = false;
    try {
      const cartSection = document.body.classList.contains('template-cart') ? window.cartT4SectionID : 'mini_cart';
      const sections = ['cart_data', cartSection].filter(Boolean);
      const response = await fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1 }], sections, sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || root.dataset.error);
      committed = true;
      message(root, root.dataset.added);
      if (window.T4SThemeSP?.Cart?.renderContents && sections.every(id => result.sections?.[id])) {
        window.T4SThemeSP.Cart.renderContents(result.sections);
      } else {
        location.assign(base + 'cart');
        return;
      }
      if (typeof window.opusRefreshCart === 'function') {
        await window.opusRefreshCart();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      scheduleRefresh();
    } catch (error) {
      if (committed) {
        location.assign(base + 'cart');
        return;
      }
      message(root, error.message || root.dataset.error);
    } finally {
      adding = false;
      if (button.isConnected) button.textContent = original;
      roots.forEach(el => el.querySelectorAll('[data-upsell-add]').forEach(btn => { btn.disabled = false; }));
    }
  }

  async function refresh() {
    const serial = ++refreshSerial;
    try {
      const response = await fetch(base + 'cart.js', { cache: 'no-store', signal: AbortSignal.timeout(10000) });
      if (!response.ok) return;
      const cart = await response.json();
      if (serial !== refreshSerial) return;
      roots.forEach(root => {
        if (!root.isConnected) { roots.delete(root); visibility.unobserve(root); return; }
        const state = states.get(root);
        const seed = String(cart.items.at(-1)?.product_id || '');
        const exclude = cart.items.map(item => String(item.product_id));
        const changed = state.seed !== seed || state.exclude.join(',') !== exclude.join(',');
        root.hidden = cart.item_count === 0;
        if (!changed) return;
        state.seed = seed;
        state.exclude = exclude;
        state.loaded = false;
        state.loading = false;
        state.serial++;
        if (root.hidden) return;
        const rect = root.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < innerHeight && root.getClientRects().length) load(root);
      });
    } catch (error) { }
  }

  function scheduleRefresh() { clearTimeout(refreshTimer); refreshTimer = setTimeout(refresh, 120); }
  function discover(container) {
    if (container.matches?.('[data-cart-upsell]')) mount(container);
    container.querySelectorAll?.('[data-cart-upsell]').forEach(mount);
  }
  let appTick = false;
  const observedAppRoots = new WeakSet();
  function mountApp() {
    appTick = false;
    const template = document.getElementById('havana-upsell-template');
    if (!template) return;
    const scopes = [document];
    const shadow = document.getElementById('opus-shadow-container')?.shadowRoot;
    if (shadow) {
      scopes.push(shadow);
      if (!observedAppRoots.has(shadow)) {
        observedAppRoots.add(shadow);
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = template.dataset.stylesheet;
        shadow.append(css);
        domObserver.observe(shadow, { childList: true, subtree: true });
      }
    }
    scopes.forEach(scope => scope.querySelectorAll('.opus-drawer-container').forEach(drawer => {
      if (drawer.querySelector('[data-cart-upsell]')) return;
      const slot = drawer.querySelector('.opus-upsell-carousel');
      const content = drawer.querySelector('.opus-scroll-items .cdr-main-color');
      if (!slot && !content) return;
      const root = template.content.firstElementChild.cloneNode(true);
      if (slot) slot.before(root); else content.append(root);
      mount(root);
      scheduleRefresh();
    }));
  }
  function scheduleApp() { if (!appTick) { appTick = true; requestAnimationFrame(mountApp); } }
  const domObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.target.closest?.('[data-cart-upsell], .opus-timer')) return;
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        discover(node);
        if (node.matches?.('.opus-drawer-container, #opus-shadow-container') || node.querySelector?.('.opus-drawer-container, #opus-shadow-container') || node.closest?.('.opus-drawer-container')) scheduleApp();
      });
      if (mutation.target.closest?.('.opus-drawer-container')) {
        scheduleApp();
        if (mutation.addedNodes.length) scheduleRefresh();
      }
    });
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
  discover(document);
  mountApp();
  document.addEventListener('cart:updated', scheduleRefresh);
  window.addEventListener('OpusCartChange', scheduleRefresh);
  window.addEventListener('OpusAddToCartComplete', scheduleRefresh);
  document.addEventListener('shopify:section:load', event => discover(event.target));
})();
